import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import IconWrapper from '../../IconWrapper';
import { FiExternalLink, FiMoreVertical, FiTrash2 } from 'react-icons/fi';
import { Simulation } from './SimulationCard';

interface CaseCardProps {
  simulation: Simulation;
  caseLabel: string;
  lastActive: string;
  summary: string;
  tags?: string[];
  onOpen: (simulationId: string) => void;
  onViewSignal: (signalId: string) => void;
  onDelete?: (simulationId: string) => void;
}

const CaseCard: React.FC<CaseCardProps> = ({
  simulation,
  caseLabel,
  lastActive,
  summary,
  tags = [],
  onOpen,
  onViewSignal,
  onDelete,
}) => {
  const handleOpen = () => {
    onOpen(simulation.id);
  };

  const handleViewSignal = (event: React.MouseEvent) => {
    event.stopPropagation();
    onViewSignal(simulation.signal_id);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete?.(simulation.id);
  };

  const showAttentionTag = simulation.status !== 'completed' && simulation.status !== 'draft';

  return (
    <Card
      hoverable
      clickable
      padding="md"
      className="min-h-[164px] rounded-lg bg-card"
      onClick={handleOpen}
    >
      <CardContent className="flex h-full flex-col px-0">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-primary">{caseLabel}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">{lastActive}</div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground">
                <IconWrapper icon={FiMoreVertical} className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleViewSignal}>
                <IconWrapper icon={FiExternalLink} className="mr-2 h-4 w-4" />
                View Signal
              </DropdownMenuItem>
              {onDelete && (
                <DropdownMenuItem onClick={handleDelete} className="text-[var(--go-berry)]">
                  <IconWrapper icon={FiTrash2} className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <p className="line-clamp-2 text-[13px] font-medium leading-[1.6] text-foreground">{summary}</p>

        {(tags.length > 0 || showAttentionTag) && (
          <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
            {tags.map((tag) => (
              <Badge key={tag} variant="vulnerability">
                {tag}
              </Badge>
            ))}
            {showAttentionTag && <Badge variant="attention">{simulation.status.replace('_', ' ')}</Badge>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CaseCard;
